/** SUNTRONIX ONE — Sales Desk MY WORK panel.
 *
 *  11 actionable counts hydrated from /api/go-live/my-work.
 *  Every tile is a drill-down link into the underlying record set.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  FileText, ClipboardCheck, Sparkles, Users, PackageSearch,
  ShoppingBag, AlertTriangle, TrendingUp, Wrench, ClipboardList,
  RefreshCw, ArrowRight,
} from "lucide-react";

const TILES = [
  { key: "draft_quotations",                 label: "Draft quotations",             icon: FileText,           to: "/commercial/crm/quotations?status=DRAFT",           tone: "sky" },
  { key: "quotations_awaiting_customer", label: "Awaiting customer",       icon: ClipboardCheck, to: "/commercial/crm/quotations?status=SENT",             tone: "amber" },
  { key: "quote_revision_required",     label: "Revision required",          icon: Sparkles,               to: "/commercial/crm/quotations?status=INTERNAL_REVIEW", tone: "violet" },
  { key: "vendor_registrations_in_progress", label: "Vendor reg. in progress", icon: Users,                       to: "/administration/corporate-kyc",                              tone: "indigo" },
  { key: "vendor_registration_missing", label: "Vendor reg. info missing", icon: AlertTriangle,     to: "/administration/corporate-kyc",                              tone: "rose" },
  { key: "customer_po_awaiting_validation", label: "PO awaiting validation", icon: ClipboardList,     to: "/commercial/customer-po?status=VALIDATION_PENDING", tone: "amber" },
  { key: "customer_po_mismatch",           label: "PO mismatch",                     icon: AlertTriangle,     to: "/commercial/customer-po?status=MISMATCH",           tone: "rose" },
  { key: "orders_ready_for_processing", label: "Orders ready",                 icon: ShoppingBag,       to: "/commercial/one-orders?status=CREATED",             tone: "emerald" },
  { key: "product_information_missing", label: "Product info missing",     icon: PackageSearch,   to: "/technology/product-master?filter=incomplete",     tone: "amber" },
  { key: "product_description_missing", label: "Description missing",         icon: Wrench,                    to: "/technology/product-master?filter=no-desc",             tone: "rose" },
  { key: "crm_sync_review_required",     label: "CRM sync review",             icon: TrendingUp,         to: "/commercial/crm/customer-import",                         tone: "violet" },
];

const TONE = {
  sky:              { badge: "bg-sky-50 text-sky-700 ring-sky-200",                     icon: "text-sky-500" },
  amber:        { badge: "bg-amber-50 text-amber-700 ring-amber-200",         icon: "text-amber-500" },
  violet:      { badge: "bg-violet-50 text-violet-700 ring-violet-200",     icon: "text-violet-500" },
  indigo:    { badge: "bg-indigo-50 text-indigo-700 ring-indigo-200",     icon: "text-indigo-500" },
  rose:          { badge: "bg-rose-50 text-rose-700 ring-rose-200",                 icon: "text-rose-500" },
  emerald:  { badge: "bg-emerald-50 text-emerald-700 ring-emerald-200", icon: "text-emerald-500" },
};

export default function MyWorkPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/go-live/my-work");
      setData(data);
    } catch { /* silent */ }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const anyPending = TILES.some((t) => (data[t.key]?.count || 0) > 0);

  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4 space-y-3"
        data-testid="my-work-panel">
      <div className="flex items-center gap-2">
        <div className="text-[13px] font-heading font-semibold text-slate-900">
          My Work
        </div>
        <span className={"text-[10px] px-1.5 py-0.5 rounded ring-1 " +
                                    (anyPending
                                        ? "bg-amber-50 text-amber-700 ring-amber-200"
                                        : "bg-emerald-50 text-emerald-700 ring-emerald-200")}>
          {anyPending ? "Action required" : "All clear"}
        </span>
        <div className="flex-1" />
        <button onClick={load} disabled={busy}
            data-testid="my-work-refresh"
            className="text-[10px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {TILES.map((t) => {
          const n = data[t.key]?.count || 0;
          const tone = TONE[t.tone] || TONE.sky;
          return (
            <button key={t.key}
                onClick={() => n > 0 && navigate(t.to)}
                disabled={n === 0}
                aria-disabled={n === 0}
                data-testid={`my-work-${t.key}`}
                className={"text-left border rounded-lg p-2.5 transition-all " +
                    (n > 0 ? "border-slate-300 bg-white hover:shadow-sm cursor-pointer"
                                : "border-slate-100 bg-slate-50/50 cursor-default opacity-70")}>
              <div className="flex items-start gap-1.5">
                <t.icon className={"h-3.5 w-3.5 shrink-0 " + tone.icon} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-slate-500 leading-tight">{t.label}</div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <div className={"text-lg font-heading font-semibold " +
                                                (n > 0 ? "text-slate-900" : "text-slate-400")}>
                      {n}
                    </div>
                    {n > 0 && <ArrowRight className="h-3 w-3 text-slate-400" />}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
